/**
 * CORE-BN — Front-of-House Attention Capacity · planning consumption interface
 *
 * Constitutional authority: CORE-BN "Human Authority" —
 *
 *   "Advisory. Front-of-house attention capacity INFORMS planning; it
 *    never blocks intake, never refuses a customer, and never reassigns a
 *    person. A shop may always operate past its modelled capacity. What
 *    it may not do is operate past it *without knowing*."
 *
 * Why this module exists as a separate contract
 * ---------------------------------------------
 * The result object is an explanation for a human. This is the shape a
 * PLANNER consumes: intake pacing, promise-setting (CORE-BG / CORE-BH),
 * board surfaces (CORE-AD / CORE-AS), and capacity recovery (CORE-BO),
 * which today evaluates capacity gain with no front-of-house term at all.
 *
 * Giving planners their own narrow contract does two things. It keeps
 * downstream capabilities from reaching into the model's internals and
 * quietly coupling to them. And it makes the advisory guarantee
 * STRUCTURAL rather than documentary: `advisory` and `blocksIntake` are
 * literal types, so there is no assignment a future author can make that
 * hands a consumer a blocking front-of-house signal. Turning CORE-BN into
 * a gate would require deleting this contract, which is a reviewable act
 * rather than an accident.
 *
 * Purity: no I/O, no clock, no React.
 */

import { DEFAULT_FOH_CLAIM_WEIGHT_MINUTES } from './policy';
import type {
  FohAttentionCapacityResult,
  FohAttentionClaimKind,
  FohEstimateConfidence,
  FohSaturationState,
} from './types';

/**
 * How hard the shop should think before adding more front-of-house work
 * to this window. Every value is a recommendation to a human or to a
 * surface that shows a human; none is an instruction to a machine.
 *
 *   - `'normal'`           — no front-of-house reason to pace intake.
 *   - `'watch'`            — headroom is thinning; worth a glance.
 *   - `'slow'`             — the shop should consider spacing arrivals.
 *   - `'hold_recommended'` — the window is already over capacity. Still
 *                            advisory: the shop may proceed, knowingly.
 *   - `'unknown'`          — no estimate. Plan exactly as today.
 */
export type FohIntakePacingHint =
  | 'normal'
  | 'watch'
  | 'slow'
  | 'hold_recommended'
  | 'unknown';

/**
 * How much front-of-house saturation should temper a promise made in
 * this window. CORE-BG / CORE-BH set promises assuming someone is free to
 * call the customer back; this says whether that assumption holds.
 */
export type FohPromiseRiskHint = 'none' | 'low' | 'elevated' | 'high' | 'unknown';

/**
 * The advisory signal.
 *
 * `advisory: true` and `blocksIntake: false` are LITERAL types, not
 * booleans. They are the contract.
 */
export interface FohAttentionPlanningSignal {
  shopId: string;
  windowStartIso: string;
  windowEndIso: string;

  /** CORE-BN is advisory in Core. Always. */
  readonly advisory: true;
  /** CORE-BN never blocks intake, refuses a customer, or reassigns staff. */
  readonly blocksIntake: false;
  /** The value is an estimate, and consumers must present it as one. */
  readonly isEstimate: true;

  /**
   * SP-05 evidence class, carried across the capability boundary.
   *
   * SP-05 requires every fact to declare what kind of truth it is, and
   * separately forbids absence of evidence being presented as evidence.
   * Both apply here:
   *
   *   `'derivation'` — the platform computed this from the shop's own
   *                    operational truth. There is an estimate.
   *   `null`         — there is no estimate, so there is no derived fact
   *                    to classify. Paired with `degradeToBaseline: true`.
   *
   * `null` is deliberately NOT `'derivation'`. Labelling an absent
   * estimate as something the platform derived would state that the shop
   * was measured and found comfortable, when in fact it was never
   * measured at all — the precise substitution SP-05's constitutional
   * rule forbids.
   */
  readonly evidenceClass: 'derivation' | null;

  state: FohSaturationState;
  intakePacing: FohIntakePacingHint;
  promiseRisk: FohPromiseRiskHint;

  /**
   * Attention-minutes left in the window. `null` when there is no
   * estimate — consumers must branch on this, not coerce it to 0.
   */
  headroomAttentionMinutes: number | null;
  utilization: number | null;
  confidence: FohEstimateConfidence;

  /**
   * The shop's applied attention cost, in minutes, for one occurrence of
   * each claim kind.
   *
   * Included because `headroomAttentionMinutes` is otherwise
   * uninterpretable to a planner. "Nine minutes spare" only becomes a
   * decision when the planner can convert its OWN backlog into the same
   * unit — four customers to call back, at this shop's cost per call, is
   * either comfortably inside nine minutes or plainly outside it.
   *
   * This is shop policy, not person data, and it is the same record the
   * result already discloses. A planner must not reinvent these numbers;
   * doing so would fork the weighting the shop configured and explain a
   * different model than the one the card shows.
   */
  claimWeightMinutes: Readonly<Record<FohAttentionClaimKind, number>>;

  /**
   * True when the shop has no usable front-of-house estimate and the
   * consumer must behave exactly as it did before CORE-BN existed.
   * Checking this one flag is the whole degradation contract.
   */
  degradeToBaseline: boolean;

  /**
   * One shop-scoped sentence a consumer may show. Never names a person.
   * Provided so a downstream surface does not have to reimplement the
   * explanation and risk dropping the contributing claims.
   */
  summary: string;
}

/**
 * Translate a capacity result into the advisory planning signal.
 *
 * Deliberately lossy: planners get the verdict, the headroom and the
 * sentence, not the claim table. A planner that wants the full
 * explanation should render the result, not re-derive it.
 */
export function deriveFohAttentionPlanningSignal(
  result: FohAttentionCapacityResult,
  summary: string,
): FohAttentionPlanningSignal {
  const unknown = result.state === 'unknown';

  return Object.freeze({
    shopId: result.shopId,
    windowStartIso: result.windowStartIso,
    windowEndIso: result.windowEndIso,
    advisory: true as const,
    blocksIntake: false as const,
    isEstimate: true as const,
    // No estimate means no derived fact to classify. See the field doc.
    evidenceClass: unknown ? null : result.evidenceClass,
    state: result.state,
    intakePacing: intakePacingFor(result.state),
    promiseRisk: promiseRiskFor(result.state),
    headroomAttentionMinutes: result.headroomAttentionMinutes,
    utilization: result.utilization,
    confidence: result.basis.confidence,
    claimWeightMinutes: result.appliedPolicy.claimWeightMinutes,
    degradeToBaseline: unknown,
    summary,
  });
}

function intakePacingFor(state: FohSaturationState): FohIntakePacingHint {
  switch (state) {
    case 'comfortable':
      return 'normal';
    case 'loaded':
      return 'watch';
    case 'saturated':
      return 'slow';
    case 'over_capacity':
      return 'hold_recommended';
    default:
      return 'unknown';
  }
}

function promiseRiskFor(state: FohSaturationState): FohPromiseRiskHint {
  switch (state) {
    case 'comfortable':
      return 'none';
    case 'loaded':
      return 'low';
    case 'saturated':
      return 'elevated';
    case 'over_capacity':
      return 'high';
    default:
      return 'unknown';
  }
}

/**
 * The signal a consumer should use when CORE-BN is switched off, has no
 * shop, or has not loaded yet.
 *
 * Its entire purpose is that a consumer never has to write
 * `if (signal) {...}` and accidentally treat "not loaded" as
 * "comfortable". The absent signal is explicitly `'unknown'` and
 * explicitly says to plan as today.
 */
export function absentFohAttentionPlanningSignal(
  shopId: string,
  windowStartIso: string,
  windowEndIso: string,
): FohAttentionPlanningSignal {
  return Object.freeze({
    shopId,
    windowStartIso,
    windowEndIso,
    advisory: true as const,
    blocksIntake: false as const,
    isEstimate: true as const,
    // Nothing was derived. SP-05: absence is never evidence of absence.
    evidenceClass: null,
    state: 'unknown' as FohSaturationState,
    intakePacing: 'unknown' as FohIntakePacingHint,
    promiseRisk: 'unknown' as FohPromiseRiskHint,
    headroomAttentionMinutes: null,
    utilization: null,
    confidence: 'none' as FohEstimateConfidence,
    // The untuned defaults. A consumer may still translate its backlog
    // into minutes for display, but `degradeToBaseline` tells it not to
    // act on the comparison.
    claimWeightMinutes: DEFAULT_FOH_CLAIM_WEIGHT_MINUTES,
    degradeToBaseline: true,
    summary:
      'Front-of-house attention capacity is not available, so the shop plans as it normally would.',
  });
}
